/**
 * Rate Limiting - Enhanced rate limiting with multiple strategies
 */

import { RATE_LIMITS as BASE_LIMITS } from '../rateLimit'

// In-memory store (use Redis in production)
const store = new Map()

// Clean up expired entries periodically
setInterval(() => {
  const now = Date.now()
  for (const [key, entry] of store) {
    if (entry.resetAt <= now) {
      store.delete(key)
    }
  }
}, 60_000)

function getKey(action, identifier) {
  return `rate:${action}:${identifier}`
}

function isBlocked(action, identifier) {
  const blockKey = `block:${action}:${identifier}`
  const block = store.get(blockKey)
  if (block && block.expiresAt > Date.now()) {
    return {
      blocked: true,
      remaining: 0,
      retryAfterMs: block.expiresAt - Date.now(),
      reason: block.reason || 'Too many requests',
    }
  }
  return { blocked: false }
}

// ✅ Enhanced rate limits with progressive blocking
export const RATE_LIMITS = {
  ...BASE_LIMITS,
  
  // Authentication
  login: { max: 5, windowMs: 60_000, blockDuration: 300_000, progressive: true },
  signup: { max: 3, windowMs: 60_000, blockDuration: 300_000, progressive: true },
  resetPassword: { max: 3, windowMs: 60_000, blockDuration: 300_000, progressive: true },
  
  // API
  api: { max: 100, windowMs: 60_000 },
  apiWrite: { max: 50, windowMs: 60_000 },
  
  // Chat
  chat: { max: 30, windowMs: 60_000, blockDuration: 120_000 },
  message: { max: 10, windowMs: 60_000 },
  
  // File uploads
  upload: { max: 10, windowMs: 60_000, blockDuration: 300_000 },
  
  // Document operations
  documentView: { max: 100, windowMs: 60_000 },
  documentUpload: { max: 20, windowMs: 60_000 },
  
  // Token operations
  tokenGenerate: { max: 10, windowMs: 60_000 },
  tokenRevoke: { max: 10, windowMs: 60_000 },
  
  // Agent requests
  agentRequest: { max: 5, windowMs: 60_000, blockDuration: 300_000 },
  
  // Payment
  payment: { max: 3, windowMs: 60_000, blockDuration: 600_000 },
  
  // ✅ Critical operations
  transactionCreate: { max: 20, windowMs: 60_000 },
  documentDelete: { max: 10, windowMs: 60_000 },
  staffInvite: { max: 5, windowMs: 60_000 },
}

export function checkRateLimit(action, identifier, options = {}) {
  const { ip, userId, skipBlock = false } = options
  
  const identifierKey = userId || ip || identifier || 'anon'
  const config = RATE_LIMITS[action]
  
  if (!config) {
    return { allowed: true, remaining: Infinity }
  }

  // Check if blocked (progressive blocking)
  if (!skipBlock) {
    const blockCheck = isBlocked(action, identifierKey)
    if (blockCheck.blocked) {
      return {
        allowed: false,
        remaining: 0,
        retryAfterMs: blockCheck.retryAfterMs,
        reason: blockCheck.reason,
      }
    }
  }

  const key = getKey(action, identifierKey)
  const now = Date.now()
  const entry = store.get(key)

  // ✅ Progressive blocking
  if (config.progressive && entry) {
    const blockKey = `block:${action}:${identifierKey}`
    const block = store.get(blockKey)
    if (block) {
      const blockLevel = block.level || 1
      const blockDuration = config.blockDuration * blockLevel
      if (now < block.expiresAt) {
        return {
          allowed: false,
          remaining: 0,
          retryAfterMs: block.expiresAt - now,
          blockLevel,
          reason: `Rate limit exceeded (Level ${blockLevel})`
        }
      }
      store.delete(blockKey)
    }
  }

  if (!entry || now > entry.resetAt) {
    store.set(key, { 
      count: 1, 
      resetAt: now + config.windowMs,
    })
    return { 
      allowed: true, 
      remaining: config.max - 1,
      resetAt: now + config.windowMs,
    }
  }

  if (entry.count >= config.max) {
    // Block this identifier if configured
    if (config.blockDuration) {
      const blockKey = `block:${action}:${identifierKey}`
      const blockEntry = store.get(blockKey)
      if (!blockEntry || blockEntry.expiresAt <= now) {
        const blockLevel = Math.floor((entry.count || 0) / config.max) + 1
        store.set(blockKey, {
          expiresAt: now + (config.blockDuration * blockLevel),
          reason: `Rate limit exceeded (Level ${blockLevel})`,
          level: blockLevel,
        })
      }
    }
    
    return {
      allowed: false,
      remaining: 0,
      retryAfterMs: entry.resetAt - now,
      reason: `Rate limit exceeded. Try again in ${Math.ceil((entry.resetAt - now) / 1000)} seconds.`,
    }
  }

  entry.count++
  return {
    allowed: true,
    remaining: config.max - entry.count,
    resetAt: entry.resetAt,
  }
}

// Rate limit middleware for API calls
export function rateLimitMiddleware(action, identifier, options = {}) {
  return function(handler) {
    return async function(...args) {
      const result = checkRateLimit(action, identifier, options)
      
      if (!result.allowed) {
        throw new Error(result.reason || 'Rate limit exceeded')
      }
      
      return handler(...args)
    }
  }
}

// Batch rate limit check
export function checkBatchRateLimit(actions, identifiers) {
  const results = {}
  
  for (const action of actions) {
    const id = identifiers[action] || 'anon'
    results[action] = checkRateLimit(action, id)
  }
  
  return results
}

// Reset rate limit
export function resetRateLimit(action, identifier) {
  const key = getKey(action, identifier)
  store.delete(key)
  const blockKey = `block:${action}:${identifier}`
  store.delete(blockKey)
}

// Get rate limit status
export function getRateLimitStatus(action, identifier) {
  const key = getKey(action, identifier)
  const entry = store.get(key)
  const config = RATE_LIMITS[action]
  
  if (!config) return null
  
  if (!entry) {
    return {
      remaining: config.max,
      total: config.max,
      resetAt: null,
    }
  }
  
  const now = Date.now()
  if (now > entry.resetAt) {
    return {
      remaining: config.max,
      total: config.max,
      resetAt: null,
    }
  }
  
  return {
    remaining: Math.max(0, config.max - entry.count),
    total: config.max,
    resetAt: entry.resetAt,
  }
}

export default {
  RATE_LIMITS,
  checkRateLimit,
  rateLimitMiddleware,
  checkBatchRateLimit,
  resetRateLimit,
  getRateLimitStatus,
}